/**
 * Pinnacle's service and discipline IDs, as the Work Queue filters use them.
 *
 * Moved here from partner's `variations/pinnacle.ts` so there is one home for the
 * vocabulary rather than a copy per consumer. Partner imports these back for the
 * queues it still declares locally, and each of those imports disappears as its
 * queue moves into this package.
 *
 * Source of truth for the IDs themselves:
 * https://docs.google.com/spreadsheets/d/14LjYsiJHynPc0TG_heeSec-F2JpPGPafoJSn8IB6K_M/edit?gid=1593865658#gid=1593865658
 */

export const SOC_BASE_SERVICE_ID = '99558';

export const SOC_OT_SERVICE_ID = '99586';

export const SOC_PT_SERVICE_ID = '99564';

export const SOC_ST_SERVICE_ID = '99575';

export const SOC_SERVICE_IDS = [
    SOC_BASE_SERVICE_ID,
    SOC_OT_SERVICE_ID,
    SOC_PT_SERVICE_ID,
    SOC_ST_SERVICE_ID,
];

export const ROC_BASE_SERVICE_ID = '99559';

export const ROC_OT_SERVICE_ID = '99570';

export const ROC_PT_SERVICE_ID = '99565';

export const ROC_ST_SERVICE_ID = '99576';

export const ROC_SERVICE_IDS = [
    ROC_BASE_SERVICE_ID,
    ROC_OT_SERVICE_ID,
    ROC_PT_SERVICE_ID,
    ROC_ST_SERVICE_ID,
];

export const PT_EVAL_SERVICE_IDS = [
    '35304', // PT Evaluation
    '98065', // PT LSVT Eval
    '98070', // PT Lymphedema Eval
    '98074', // PT Vestibular RT Eval
];

export const OT_EVAL_SERVICE_IDS = [
    '73472', // OT Eval Lymphedema
    '28387', // OT Evaluation
];

export const ST_EVAL_SERVICE_IDS = [
    '31742', // ST Eval
];

export const OASIS_E2_FOLLOWUP_BASE_SERVICE_ID = '99561';

export const EVAL_SERVICE_IDS = [
    ...PT_EVAL_SERVICE_IDS,
    ...OT_EVAL_SERVICE_IDS,
    ...ST_EVAL_SERVICE_IDS,
    '4506', // MSW Evaluation
    OASIS_E2_FOLLOWUP_BASE_SERVICE_ID,
];

export const OASIS_DISCHARGE_BASE_SERVICE_ID = '99562';

export const OASIS_DISCHARGE_OT_SERVICE_ID = '99573';

export const OASIS_DISCHARGE_PT_SERVICE_ID = '99568';

export const OASIS_DISCHARGE_ST_SERVICE_ID = '99579';

export const OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID = '99585';

export const OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID = '99556';

export const OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID = '99555';

export const OASIS_DISCHARGE_NON_BILLABLE_ST_SERVICE_ID = '99557';

export const OASIS_DISCHARGE_ONLY_SERVICE_IDS = [
    OASIS_DISCHARGE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_OT_SERVICE_ID,
    OASIS_DISCHARGE_PT_SERVICE_ID,
    OASIS_DISCHARGE_ST_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_ST_SERVICE_ID,
];

export const OASIS_DEATH_BASE_SERVICE_ID = '99563';

export const OASIS_DEATH_OT_SERVICE_ID = '99574';

export const OASIS_DEATH_PT_SERVICE_ID = '99569';

export const OASIS_DEATH_ST_SERVICE_ID = '99580';

export const OASIS_DISCHARGE_SERVICE_IDS = [
    ...OASIS_DISCHARGE_ONLY_SERVICE_IDS,
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_OT_SERVICE_ID,
    OASIS_DEATH_PT_SERVICE_ID,
    OASIS_DEATH_ST_SERVICE_ID,
];

export const RECERT_BASE_SERVICE_ID = '99581';

export const RECERT_OT_SERVICE_ID = '99583';

export const RECERT_PT_SERVICE_ID = '99582';

export const RECERT_ST_SERVICE_ID = '99584';

export const OASIS_RECERT_SERVICE_IDS = [
    RECERT_BASE_SERVICE_ID,
    RECERT_OT_SERVICE_ID,
    RECERT_PT_SERVICE_ID,
    RECERT_ST_SERVICE_ID,
];

export const SOC_RELATED_NON_VISIT_TASKS = [
    '398', // Consent
    '100131', // Patient Consent
    '98497', // Disaster Plan
    '417', // Medication Profile
];

export const NOMNC_SERVICE_IDS = ['2456', '60332'];

export const NON_VISIT_TASKS_SERVICE_IDS_TO_IGNORE_POST_DISCHARGE = [
    ...NOMNC_SERVICE_IDS, // Notice of Medicare Non-Coverage, NOMNC
    '99675', // 60-Day Conference
    '248', // CMS 485
    '96785', // Delay in SOC Communication
    '60517', // SCIC
    '256', // Post Hospital Order
    '6848', // Patient Communication
    '100740', // Client Alert
    '328', // Other Document
    '98294', // Welcome Call Communication Note
];

export const DISCHARGE_SUMMARY_RN_SERVICE_ID = '81740';

export const DISCHARGE_SUMMARY_OT_SERVICE_ID = '25372';

export const DISCHARGE_SUMMARY_PT_SERVICE_ID = '25375';

export const DISCHARGE_SUMMARY_ST_SERVICE_ID = '25376';

export const DISCHARGE_SUMMARY_MSW_SERVICE_ID = '25424';

export const DISCHARGE_SUMMARY_SERVICE_IDS = [
    DISCHARGE_SUMMARY_RN_SERVICE_ID,
    DISCHARGE_SUMMARY_OT_SERVICE_ID,
    DISCHARGE_SUMMARY_PT_SERVICE_ID,
    DISCHARGE_SUMMARY_ST_SERVICE_ID,
    DISCHARGE_SUMMARY_MSW_SERVICE_ID,
];

export const NON_BILLABLE_SUPERVISORY_VISIT_RN_SERVICE_ID = '98374';

export const NON_BILLABLE_SUPERVISORY_VISIT_OT_SERVICE_ID = '98381';

export const NON_BILLABLE_SUPERVISORY_VISIT_PT_SERVICE_ID = '98385';

export const NON_BILLABLE_SUPERVISORY_VISIT_SERVICE_IDS = [
    NON_BILLABLE_SUPERVISORY_VISIT_RN_SERVICE_ID,
    NON_BILLABLE_SUPERVISORY_VISIT_OT_SERVICE_ID,
    NON_BILLABLE_SUPERVISORY_VISIT_PT_SERVICE_ID,
];

export const POST_DISCHARGE_EXCLUDED_SERVICE_IDS = [
    ...SOC_RELATED_NON_VISIT_TASKS,
    ...NON_VISIT_TASKS_SERVICE_IDS_TO_IGNORE_POST_DISCHARGE,
    ...DISCHARGE_SUMMARY_SERVICE_IDS,
    ...NON_BILLABLE_SUPERVISORY_VISIT_SERVICE_IDS,
];

export const OASIS_E2_FOLLOWUP_OT_SERVICE_ID = '99572';

export const OASIS_E2_FOLLOWUP_PT_SERVICE_ID = '99567';

export const OASIS_E2_FOLLOWUP_ST_SERVICE_ID = '99578';

/** OASIS-E2 Follow-up, the SCIC anchor. One per discipline. */
export const OASIS_E2_FOLLOWUP_SERVICE_IDS = [
    OASIS_E2_FOLLOWUP_BASE_SERVICE_ID,
    OASIS_E2_FOLLOWUP_OT_SERVICE_ID,
    OASIS_E2_FOLLOWUP_PT_SERVICE_ID,
    OASIS_E2_FOLLOWUP_ST_SERVICE_ID,
];

export const CMS_485_SERVICE_ID = '248';

export const SCIC_ORDER_SERVICE_ID = '60517';

export const POST_HOSPITAL_ORDER_SERVICE_ID = '256';

export const OT_DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    '3681', // OT Discharge Eval w/ Discharge Summary
    DISCHARGE_SUMMARY_OT_SERVICE_ID,
];

export const PT_DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    '35055', // PT Discharge w/Discharge Summary
    DISCHARGE_SUMMARY_PT_SERVICE_ID,
];

export const SN_DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    '29547', // SNV W/ Discharge Summary
    DISCHARGE_SUMMARY_RN_SERVICE_ID,
];

export const ST_DISCIPLINE_DISCHARGE_SERVICE_IDS = [DISCHARGE_SUMMARY_ST_SERVICE_ID];

export const MSW_DISCIPLINE_DISCHARGE_SERVICE_IDS = [DISCHARGE_SUMMARY_MSW_SERVICE_ID];

export const ALL_DISCIPLINE_DISCHARGE_SERVICE_IDS = [
    ...OT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...PT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...SN_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...ST_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ...MSW_DISCIPLINE_DISCHARGE_SERVICE_IDS,
];

/** A completed Eval or ROC in the same discipline re-admits and nullifies a discharge. */
export const DISCIPLINE_READMISSION_SERVICE_IDS = [...EVAL_SERVICE_IDS, ...ROC_SERVICE_IDS];

/**
 * Codes that satisfy the re-eval requirement: re-evals, SOC/ROC/Recert/Eval, plus
 * discharge and death visits, which also satisfy it.
 */
export const REASSESSMENT_SERVICE_IDS = [
    '28388', // OT Re-Evaluation
    '28300', // OT Re-Evaluation w/Supervisory Visit
    '98113', // OT Re-Eval Lymphedema
    '83723', // PT Re-Evaluation
    '35172', // PT Re-evaluation w/Supervisory Visit
    '98067', // PT Lymphedema Re-Eval
    '31744', // ST Re-Evaluation
    '99562', // OASIS-E2 Discharge
    '99585', // OASIS-E2 Discharge (Non-Billable)
    '99563', // OASIS-E2 Death
    '99568', // OASIS-E2 Discharge (PT)
    '99555', // OASIS-E2 Discharge (Non-Billable) - PT
    '99569', // OASIS-E2 Death (PT)
    '99573', // OASIS-E2 Discharge (OT)
    '99556', // OASIS-E2 Discharge (Non-Billable) - OT
    '99574', // OASIS-E2 Death (OT)
    ...SOC_SERVICE_IDS,
    ...ROC_SERVICE_IDS,
    ...OASIS_RECERT_SERVICE_IDS,
    ...EVAL_SERVICE_IDS,
];

export const TRANSFER_SERVICE_IDS = [
    '99560', // OASIS-E2 Transfer
    '99571', // OASIS-E2 Transfer (OT)
    '99566', // OASIS-E2 Transfer (PT)
    '99577', // OASIS-E2 Transfer (ST)
    '98375', // Transfer Summary (RN)
    '98382', // Transfer Summary (OT)
    '98386', // Transfer Summary (PT)
    '25481', // Transfer Summary (ST)
];

/**
 * Codes that resolve an on-hold (transferred) episode, so a visit carrying one is
 * not a pending visit to cancel. Intentionally excludes ST variants.
 */
export const TRANSFER_RESOLUTION_SERVICE_IDS = [
    ROC_OT_SERVICE_ID,
    ROC_PT_SERVICE_ID,
    OASIS_DISCHARGE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_OT_SERVICE_ID,
    OASIS_DISCHARGE_PT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID,
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_OT_SERVICE_ID,
    OASIS_DEATH_PT_SERVICE_ID,
];

/** PTA visits, the anchor for the PTA branch of the therapy supervisory queue. */
export const PTA_VISIT_SERVICE_IDS = [
    '98077', // PTA LSVT Visit
    '35846', // PTA Lymphedema Visit
    '62949', // PTA Maintenance Visit
    '64960', // PTA Telehealth
    '98081', // PTA Vestibular RT Visit
    '6507', // PTA Visit
];

/** OTA / COTA visits, covered by OT supervisory visits. */
export const OTA_VISIT_SERVICE_IDS = [
    '98116', // OTA Lymphedema Visit
    '86793', // OTA Maintenance Visit
    '98118', // OTA Telehealth
    '87101', // OTA Visit
];

export const PT_THERAPY_SUPERVISORY_SERVICE_IDS = [
    NON_BILLABLE_SUPERVISORY_VISIT_PT_SERVICE_ID,
    RECERT_PT_SERVICE_ID,
    ROC_PT_SERVICE_ID,
    SOC_PT_SERVICE_ID,
    ...PT_EVAL_SERVICE_IDS,
    '35172', // PT Re-evaluation w/Supervisory Visit
    '98387', // PT Visit w/Supervisory Visit
    OASIS_DISCHARGE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_PT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID,
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_PT_SERVICE_ID,
    ...PT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
];

export const OT_THERAPY_SUPERVISORY_SERVICE_IDS = [
    NON_BILLABLE_SUPERVISORY_VISIT_OT_SERVICE_ID,
    RECERT_OT_SERVICE_ID,
    ROC_OT_SERVICE_ID,
    SOC_OT_SERVICE_ID,
    ...OT_EVAL_SERVICE_IDS,
    '28300', // OT Re-Evaluation w/Supervisory Visit
    '3680', // OT Visit w/ Supervisory Visit
    OASIS_DISCHARGE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_OT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID,
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_OT_SERVICE_ID,
    ...OT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
];

export const HHA_SUPERVISORY_SERVICE_IDS = [
    '2941', // Aide Supervisory Visit
    '98376', // RN Visit w/Supervisory Visit
    '35172', // PT Re-evaluation w/Supervisory Visit
    '98387', // PT Visit w/Supervisory Visit
    '28300', // OT Re-Evaluation w/Supervisory Visit
    '3680', // OT Visit w/ Supervisory Visit
    SOC_BASE_SERVICE_ID,
    SOC_OT_SERVICE_ID,
    SOC_PT_SERVICE_ID,
    ...NON_BILLABLE_SUPERVISORY_VISIT_SERVICE_IDS,
    ...OASIS_RECERT_SERVICE_IDS,
    ...ROC_SERVICE_IDS,
    ...OASIS_DISCHARGE_SERVICE_IDS,
];

/** Regular HHA visits, the anchor for the pending HHA supervisory queue. */
export const HHA_VISIT_SERVICE_IDS = [
    '29565', // HHA Visit
];

/**
 * LPN supervisory codes: the RN supervisory codes plus OASIS-E2 Recert and ROC,
 * which count as supervisory for an LPN, plus discharge and death visits.
 */
export const LPN_SUPERVISORY_SERVICE_IDS = [
    '99558', // OASIS-E2 Start of Care
    OASIS_E2_FOLLOWUP_BASE_SERVICE_ID,
    '98374', // Non-Billable Supervisory Visit (RN)
    '98376', // RN Visit w/Supervisory Visit
    '99581', // OASIS-E2 Recertification
    '99559', // OASIS-E2 Resumption of Care
    '29547', // SNV W/ Discharge Summary
    '81740', // Discharge Summary (RN)
    '99562', // OASIS-E2 Discharge
    '99585', // OASIS-E2 Discharge (Non-Billable)
    '99563', // OASIS-E2 Death
    '99568', // OASIS-E2 Discharge (PT)
    '99555', // OASIS-E2 Discharge (Non-Billable) - PT
    '99569', // OASIS-E2 Death (PT)
    '99573', // OASIS-E2 Discharge (OT)
    '99556', // OASIS-E2 Discharge (Non-Billable) - OT
    '99574', // OASIS-E2 Death (OT)
];

/** Regular LPN visits, the anchor for the pending LPN supervisory queue. */
export const LPN_VISIT_SERVICE_IDS = [
    '98099', // LPN Skilled Visit
    '98094', // High-Tech LPN Visit
    '77370', // LPN Foley Change
    '98098', // LPN Phone
    '98100', // LPN Telehealth
    '98101', // LPN Train/Educate
];

/** Pure paperwork and admin tasks that are never a real scheduled visit. */
export const NON_VISIT_TASK_ONLY_SERVICE_IDS = [
    ...DISCHARGE_SUMMARY_SERVICE_IDS,
    ...SOC_RELATED_NON_VISIT_TASKS,
    ...NON_VISIT_TASKS_SERVICE_IDS_TO_IGNORE_POST_DISCHARGE,
    '390', // Aide Care Plan
    ...NON_BILLABLE_SUPERVISORY_VISIT_SERVICE_IDS,
    // Transfer Summaries (RN/OT/PT/ST). Documents, not OASIS Transfer visits.
    '98375',
    '98382',
    '98386',
    '25481',
    // OASIS-E1 Discharge (Non-Billable) (RN/OT/PT/ST)
    '92504',
    '92475',
    '92474',
    '92476',
    // OASIS-E2 Discharge (Non-Billable) (RN/OT/PT/ST)
    OASIS_DISCHARGE_NON_BILLABLE_BASE_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_OT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_PT_SERVICE_ID,
    OASIS_DISCHARGE_NON_BILLABLE_ST_SERVICE_ID,
];

/** Sync error types that mean a visit is blocked on an authorization. */
export const AUTH_RELATED_SYNC_ERROR_TYPES = [
    'PINNACLE_VISIT_NOT_AUTHORIZED_ERROR',
    'PINNACLE_VISIT_WELLSKY_REJECTED',
    'PINNACLE_VISIT_BLOCKED_BY_HIGHER_AUTH_PRIORITY_VISIT',
];

/**
 * Fulfillment windows ending on or before this date predate Pinnacle's go-live and
 * are excluded from the Frequency Review Needed queue.
 */
export const PINNACLE_FULFILLMENT_WINDOW_END_CUTOFF = '2026-05-24';

/** The default look-back applied to queues that bound how far into the past they reach. */
export const DEFAULT_CONTAINER_FILTER_WINDOW_DAYS = 31;
